import React, { useState, useEffect, useCallback } from 'react';
import { Send, Check, X, Ban, RotateCcw, Mail, Building2, ShieldCheck } from 'lucide-react';
import APIService from '../services/api';
import { ListesDiffusion, DiffusionsEnCours } from './DiffusionGestion';

const BASE = 'https://besoins-tat-production.up.railway.app';
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

// Domaines de sourcing (spec §2.1) — libellés pour les clés éventuelles du back.
const DOMAIN_LABELS = {
  it: 'IT / Engineering', engineering: 'IT / Engineering', finance: 'Finance & Banque',
  banque: 'Finance & Banque', pharma: 'Pharma', data: 'Data & IA', ia: 'Data & IA',
  cyber: 'Cybersécurité', erp: 'ERP / SAP', sap: 'ERP / SAP', direction: 'Direction', juridique: 'Juridique',
};
const domainLabel = (d) => DOMAIN_LABELS[String(d).toLowerCase()] || d;

const TABS = [
  { id: 'pending_review', label: 'En attente' },
  { id: 'active', label: 'Actifs' },
  { id: 'suspended', label: 'Suspendus' },
];

const norm = (s) => ({
  id: s.id,
  firstName: s.first_name || s.firstName || s.prenom || '',
  lastName: s.last_name || s.lastName || s.nom || '',
  email: s.email || '',
  company: s.company || s.societe || '',
  domains: Array.isArray(s.domains) ? s.domains : (typeof s.domains === 'string' ? s.domains.split(/[,;]/).map(x => x.trim()).filter(Boolean) : []),
  legal: (s.legal && typeof s.legal === 'object' && !Array.isArray(s.legal)) ? s.legal : {},
  status: (s.status || 'pending_review').toLowerCase(),
  createdAt: s.created_at || s.createdAt || null,
  // Taux de commission PROPRE au sourceur — null = taux par défaut de l'org.
  commissionRate: (() => { const v = parseFloat(s.commission_rate); return Number.isFinite(v) && v > 0 && v <= 100 ? v : null; })(),
  // Ce que le sourceur touche RÉELLEMENT (taux propre ou défaut résolu par le
  // serveur) et d'où il vient — c'est cette valeur qu'on affiche en repli.
  commissionEffective: (() => { const v = parseFloat(s.commission_rate_effective); return Number.isFinite(v) ? v : null; })(),
  commissionSource: s.commission_source === 'sourceur' ? 'sourceur' : (s.commission_source === 'defaut' ? 'defaut' : null),
  // MODÈLE de rémunération : un taux ne dit pas sur quoi il porte. « 8 % de la
  // marge sur toute la mission » et « 15 % du salaire annuel, une fois » ne
  // sont pas la même promesse — c'est l'assiette EFFECTIVE que lit le sourceur.
  commissionModel: s.commission_model || null,            // propre (null = défaut org)
  commissionBasis: s.commission_basis || null,            // propre (null = défaut org)
  commissionModelEffective: s.commission_model_effective || null,
  commissionModelLibelle: s.commission_model_libelle || null,
  commissionBasisEffective: s.commission_basis_effective || null,
});

// legal{} : JSONB libre saisi à l'inscription sur sourcia.ch (non validé côté back).
// Clés attendues (France/Suisse) — mais on itère défensivement sur ce qui est présent.
const LEGAL_LABELS = { ide: 'IDE', siret: 'SIRET', tva: 'TVA', pays: 'Pays', forme_juridique: 'Forme juridique' };
const legalLabel = (k) => LEGAL_LABELS[String(k).toLowerCase()] || String(k).replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
const legalEntries = (legal) => Object.entries(legal || {})
  .filter(([, v]) => v != null && String(v).trim() !== '')
  .map(([k, v]) => [legalLabel(k), String(v).trim()]);

export default function SourceursSourcia({ showNotification }) {
  const [tab, setTab] = useState('pending_review');
  const [sourceurs, setSourceurs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  // Durée de propriété des CV (mois) — app_config 'sourcia_cv_ownership_months',
  // défaut 3. Exposée à la plateforme via GET /api/sourcia/config : un CV déposé
  // reste la propriété du sourceur pendant ce délai (commission due même si le
  // profil est placé sur une autre opportunité).
  const [ownership, setOwnership] = useState(3);
  const [ownershipSaving, setOwnershipSaving] = useState(false);

  // Rémunération des sourceurs — app_config 'sourcia_commission_rate' (%) et
  // 'sourcia_commission_basis' (texte qui SUIT le taux). sourcia affiche la
  // phrase « {taux} % {assiette} » partout ; ses replis : 8 % / « du montant
  // facturé la première année ».
  const [commission, setCommission] = useState({ rate: '', basis: '', model: '' });
  const [commissionSaving, setCommissionSaving] = useState(false);
  // Le catalogue des modèles vient du SERVEUR : le jour où un modèle s'ajoute,
  // il apparaît ici sans livraison front.
  const [modeles, setModeles] = useState([]);
  const [mdDraft, setMdDraft] = useState({});   // id → modèle propre en cours d'édition
  const [baDraft, setBaDraft] = useState({});   // id → assiette propre en cours d'édition
  // Taux propre par sourceur : brouillons de saisie par id, enregistrement unitaire.
  const [txDraft, setTxDraft] = useState({});
  const [txBusy, setTxBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/sourceurs`, { headers: authH() });
      const j = await res.json().catch(() => ({}));
      const arr = j.sourceurs || j.data || (Array.isArray(j) ? j : []);
      setSourceurs((Array.isArray(arr) ? arr : []).map(norm));
    } catch { setSourceurs([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Le catalogue des modèles de rémunération, servi par le serveur. Absent
  // (route pas encore livrée), l'écran garde le taux et l'assiette : on
  // n'invente pas une liste de modèles côté front, elle deviendrait fausse.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE}/sourceurs/commission-modeles`, { headers: authH() });
        if (!res.ok) return;
        const j = await res.json().catch(() => ({}));
        if (Array.isArray(j.data)) setModeles(j.data);
      } catch { /* l'écran reste utilisable sans le catalogue */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE}/config`, { headers: authH() });
        const cfg = await res.json().catch(() => ({}));
        const raw = cfg?.sourcia_cv_ownership_months ?? cfg?.data?.sourcia_cv_ownership_months;
        const n = parseInt(typeof raw === 'object' && raw !== null ? raw.value ?? raw : raw, 10);
        if (Number.isFinite(n) && n >= 1 && n <= 24) setOwnership(n);
        const lireCle = (k) => { const v = cfg?.[k] ?? cfg?.data?.[k]; return typeof v === 'object' && v !== null ? v.value ?? '' : v ?? ''; };
        const taux = parseFloat(String(lireCle('sourcia_commission_rate')).replace(',', '.'));
        setCommission({ rate: Number.isFinite(taux) && taux > 0 && taux <= 100 ? String(taux) : '',
          basis: String(lireCle('sourcia_commission_basis') || '').trim(),
          model: String(lireCle('sourcia_commission_model') || '').trim() });
      } catch { /* défaut 3 */ }
    })();
  }, []);

  const saveOwnership = async () => {
    const n = parseInt(ownership, 10);
    if (!Number.isFinite(n) || n < 1 || n > 24) { showNotification?.('Durée invalide (1 à 24 mois)', 'error'); return; }
    setOwnershipSaving(true);
    try {
      const res = await fetch(`${BASE}/config/sourcia_cv_ownership_months`, {
        method: 'PUT', headers: jsonH(), body: JSON.stringify({ value: n }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) throw new Error(j.error || `HTTP ${res.status}`);
      showNotification?.(`Durée de propriété des CV : ${n} mois`, 'success');
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setOwnershipSaving(false); }
  };

  // Vide = retour au taux par défaut (commission_rate: null côté serveur).
  const saveTauxSourceur = async (s) => {
    const nomS = `${s.firstName} ${s.lastName}`.trim() || s.email || 'Sourceur';
    const brut = String(txDraft[s.id] ?? '').trim().replace(',', '.');
    const taux = brut === '' ? null : parseFloat(brut);
    if (taux != null && (!Number.isFinite(taux) || taux <= 0 || taux > 100)) { showNotification?.('Taux invalide — un pourcentage entre 0 et 100, ou vide pour revenir au taux par défaut.', 'error'); return; }
    setTxBusy(s.id);
    try {
      // On n'envoie QUE ce qui a été touché : le serveur ne modifie pas un
      // champ absent, donc changer le taux n'efface pas le modèle négocié
      // avec cette société.
      const corps = { commission_rate: taux };
      if (mdDraft[s.id] !== undefined) corps.commission_model = mdDraft[s.id] || null;
      if (baDraft[s.id] !== undefined) corps.commission_basis = String(baDraft[s.id] || '').trim() || null;
      const res = await fetch(`${BASE}/sourceurs/${s.id}`, { method: 'PUT', headers: jsonH(), body: JSON.stringify(corps) });
      if (res.status === 404 || res.status === 405 || res.status === 501) { showNotification?.('Le taux par sourceur arrive côté serveur — rien n\'a été enregistré.', 'error'); return; }
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) throw new Error(j.error || `HTTP ${res.status}`);
      const maj = j.data ? norm(j.data) : { ...s, commissionRate: taux };
      setSourceurs(prev => prev.map(x => x.id === s.id ? maj : x));
      setTxDraft(p => { const n = { ...p }; delete n[s.id]; return n; });
      setMdDraft(p => { const n = { ...p }; delete n[s.id]; return n; });
      setBaDraft(p => { const n = { ...p }; delete n[s.id]; return n; });
      showNotification?.(taux == null
        ? `${nomS} : retour au taux par défaut${maj.commissionEffective != null ? ` — ${String(maj.commissionEffective).replace('.', ',')} %` : ''}.`
        : `${nomS} : commission ${String(taux).replace('.', ',')} %.`, 'success');
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setTxBusy(null); }
  };

  const saveCommission = async () => {
    const taux = parseFloat(String(commission.rate).replace(',', '.'));
    if (!Number.isFinite(taux) || taux <= 0 || taux > 100) { showNotification?.('Taux de commission invalide — un pourcentage entre 0 et 100 (ex. 8 ou 12,5).', 'error'); return; }
    const assiette = commission.basis.trim();
    if (!assiette) { showNotification?.('Décrivez l\'assiette : le texte suit le taux — ex. « du montant facturé la première année ».', 'error'); return; }
    setCommissionSaving(true);
    try {
      const cles = [['sourcia_commission_rate', taux], ['sourcia_commission_basis', assiette]];
      if (commission.model) cles.push(['sourcia_commission_model', commission.model]);
      for (const [cle, value] of cles) {
        const res = await fetch(`${BASE}/config/${cle}`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ value }) });
        const j = await res.json().catch(() => ({}));
        if (!res.ok || j.success === false) throw new Error(j.error || `HTTP ${res.status}`);
      }
      showNotification?.(`Rémunération sourceurs : ${String(taux).replace('.', ',')} % ${assiette}`, 'success');
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setCommissionSaving(false); }
  };

  const setStatus = async (s, status, okMsg) => {
    setBusyId(s.id);
    try {
      const res = await fetch(`${BASE}/sourceurs/${s.id}/status`, { method: 'PUT', headers: jsonH(), body: JSON.stringify({ status }) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j.success === false) throw new Error(j.error || `HTTP ${res.status}`);
      // Le back renvoie le sourceur à jour (data) + email_sent quand l'e-mail de
      // validation est parti — on le confirme explicitement à l'admin.
      const updated = j.data ? norm(j.data) : { ...s, status };
      setSourceurs(prev => prev.map(x => x.id === s.id ? updated : x));
      showNotification?.(okMsg, 'success');
      if (j.email_sent === true) showNotification?.('Email de validation envoyé', 'success');
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusyId(null); }
  };

  const counts = { pending_review: 0, active: 0, suspended: 0 };
  sourceurs.forEach(s => { if (counts[s.status] != null) counts[s.status] += 1; });
  const list = sourceurs.filter(s => s.status === tab);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
          <Send className="w-4 h-4 text-indigo-600" /> Sourceurs (sourcia)
        </h2>
        {counts.pending_review > 0 && (
          <span className="text-xs font-semibold bg-red-50 text-red-600 rounded-full px-2.5 py-1">{counts.pending_review} à valider</span>
        )}
      </div>
      <p className="text-sm text-gray-500">Demandes d'inscription des sourceurs freelances à votre liste de diffusion. Validez une demande pour que le sourceur reçoive vos besoins diffusés (validation manuelle, sous 48 h).</p>

      {/* ── Durée de propriété des CV (ownership) ── */}
      <div className="flex items-start gap-3 border border-gray-200 rounded-xl px-4 py-3.5 bg-gray-50">
        <ShieldCheck className="w-4 h-4 text-indigo-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-gray-800 text-sm">Durée de propriété des CV</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Un CV déposé reste la propriété du sourceur pendant ce délai : s'il est placé sur une
            <strong> autre opportunité</strong> (même sans sollicitation), la commission lui est due.
            Passé ce délai après le dépôt, elle ne l'est plus. Affiché aux sourceurs sur la plateforme.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <input type="number" min={1} max={24} value={ownership} onChange={e => setOwnership(e.target.value)}
            className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center focus:ring-2 focus:ring-indigo-400 outline-none" />
          <span className="text-xs text-gray-500">mois</span>
          <button onClick={saveOwnership} disabled={ownershipSaving}
            className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-black disabled:opacity-40 transition">
            {ownershipSaving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* ── Rémunération des sourceurs (taux + assiette, publiés à sourcia) ── */}
      <div style={{ border: '1px solid var(--border)', borderRadius: '12px', padding: '14px 16px', background: 'var(--panel)' }}>
        <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>Rémunération des sourceurs</p>
        <p style={{ margin: '3px 0 0', fontSize: '11.5px', lineHeight: 1.5, color: 'var(--text-2)' }}>
          sourcia affiche partout la phrase « <b style={{ color: 'var(--text)' }}>{(commission.rate || '8').replace('.', ',')} % {commission.basis || 'du montant facturé la première année'}</b> ». Rédigez l'assiette pour qu'elle se lise à la suite du taux — commencez par « du… / de la… », sans répéter le pourcentage. Vide, sourcia garde ses valeurs de repli (8 % · première année). Ce taux est le <b style={{ color: 'var(--text)' }}>défaut</b> : chaque sourceur actif peut recevoir le sien, sous son nom dans la liste.
        </p>
        <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {modeles.length > 0 && (
            <select value={commission.model} onChange={e => setCommission(p => ({ ...p, model: e.target.value }))}
              aria-label="Modèle de rémunération par défaut"
              style={{ border: '1px solid var(--input-border)', borderRadius: '7px', background: 'var(--input-bg)', color: 'var(--text)', padding: '6px 8px', fontSize: '12px', fontFamily: 'inherit', outline: 'none' }}>
              {modeles.map(m => <option key={m.valeur} value={m.valeur}>{m.libelle}</option>)}
            </select>
          )}
          <input type="number" min="0.01" max="100" step="0.01" value={commission.rate} onChange={e => setCommission(p => ({ ...p, rate: e.target.value }))}
            placeholder="8" aria-label="Taux de commission (%)"
            style={{ width: '76px', textAlign: 'right', fontFamily: "'IBM Plex Mono',monospace", border: '1px solid var(--input-border)', borderRadius: '8px', background: 'var(--input-bg)', color: 'var(--text)', padding: '7px 9px', fontSize: '12.5px', outline: 'none' }} />
          <span style={{ fontSize: '12.5px', color: 'var(--text-2)', fontWeight: 600 }}>%</span>
          <input value={commission.basis} onChange={e => setCommission(p => ({ ...p, basis: e.target.value }))}
            placeholder="du montant facturé la première année" aria-label="Assiette de la commission"
            style={{ flex: '1 1 280px', border: '1px solid var(--input-border)', borderRadius: '8px', background: 'var(--input-bg)', color: 'var(--text)', padding: '7px 10px', fontSize: '12.5px', fontFamily: 'inherit', outline: 'none' }} />
          <button onClick={saveCommission} disabled={commissionSaving}
            style={{ padding: '7px 13px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, cursor: 'pointer', opacity: commissionSaving ? 0.6 : 1 }}>
            {commissionSaving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-gray-200">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3.5 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === t.id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            {t.label} <span className="ml-1 text-xs font-mono text-gray-400">{counts[t.id]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-10 text-sm text-gray-400">Chargement…</div>
      ) : list.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-400 border border-dashed border-gray-200 rounded-xl">
          {tab === 'pending_review' ? 'Aucune demande en attente.' : tab === 'active' ? 'Aucun sourceur actif.' : 'Aucun sourceur suspendu.'}
        </div>
      ) : (
        <div className="space-y-2.5">
          {list.map(s => {
            const busy = busyId === s.id;
            const name = `${s.firstName} ${s.lastName}`.trim() || s.email || 'Sourceur';
            const initials = (name.match(/\b\w/g) || ['?']).slice(0, 2).join('').toUpperCase();
            return (
              <div key={s.id} className={`flex items-start gap-3 border border-gray-200 rounded-xl px-4 py-3 ${busy ? 'opacity-60' : ''}`}>
                <span className="w-9 h-9 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-bold uppercase flex-shrink-0">{initials}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 text-sm">{name}</p>
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-0.5 text-xs text-gray-500">
                    {s.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {s.email}</span>}
                    {s.company && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {s.company}</span>}
                    {s.createdAt && <span>Inscrit le {new Date(s.createdAt).toLocaleDateString('fr-FR')}</span>}
                  </div>
                  {s.domains.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {s.domains.map((d, i) => <span key={i} className="text-[11px] font-medium bg-gray-100 text-gray-600 rounded px-2 py-0.5">{domainLabel(d)}</span>)}
                    </div>
                  )}
                  {/* Infos légales de la société — à vérifier avant de valider une inscription */}
                  {(() => {
                    const entries = legalEntries(s.legal);
                    if (entries.length > 0) {
                      return (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs">
                          {entries.map(([label, value], i) => (
                            <span key={i} className="text-gray-500">{label} : <span className="font-mono text-gray-700">{value}</span></span>
                          ))}
                        </div>
                      );
                    }
                    if (s.status === 'pending_review') {
                      return <p className="mt-2 text-xs font-medium text-amber-700 bg-amber-50 rounded px-2 py-1 inline-block">Aucune info légale fournie</p>;
                    }
                    return null;
                  })()}
                  {/* Taux de commission PROPRE au sourceur — vide, le taux par
                      défaut de l'organisation s'applique. */}
                  {s.status === 'active' && (() => {
                    const brouillon = txDraft[s.id];
                    const valeur = brouillon !== undefined ? brouillon : (s.commissionRate != null ? String(s.commissionRate) : '');
                    const modeleValeur = mdDraft[s.id] !== undefined ? mdDraft[s.id] : (s.commissionModel || '');
                    const assietteValeur = baDraft[s.id] !== undefined ? baDraft[s.id] : (s.commissionBasis || '');
                    const modifie = (brouillon !== undefined && brouillon !== (s.commissionRate != null ? String(s.commissionRate) : ''))
                      || (mdDraft[s.id] !== undefined && mdDraft[s.id] !== (s.commissionModel || ''))
                      || (baDraft[s.id] !== undefined && baDraft[s.id] !== (s.commissionBasis || ''));
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginTop: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>Commission</span>
                        <input type="number" min="0.01" max="100" step="0.01" value={valeur} placeholder={String(commission.rate || 8)}
                          onChange={e => setTxDraft(p => ({ ...p, [s.id]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter' && modifie) saveTauxSourceur(s); }}
                          aria-label={`Taux de commission de ${name}`}
                          style={{ width: '64px', textAlign: 'right', fontFamily: "'IBM Plex Mono',monospace", border: '1px solid var(--input-border)', borderRadius: '7px', background: 'var(--input-bg)', color: 'var(--text)', padding: '4px 7px', fontSize: '12px', outline: 'none' }} />
                        <span style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-2)' }}>%</span>
                        {valeur === '' && !modifie && <span style={{ fontSize: '10.5px', color: 'var(--text-faint)' }}>taux par défaut ({String(s.commissionEffective ?? (commission.rate || 8)).replace('.', ',')} %)</span>}
                        {/* MODÈLE propre à ce sourceur — c'est ce qui permet le
                            versement unique d'une société sans toucher au défaut.
                            Vide = le modèle de l'organisation s'applique. */}
                        {modeles.length > 0 && (
                          <select value={modeleValeur} onChange={e => setMdDraft(p => ({ ...p, [s.id]: e.target.value }))}
                            aria-label={`Modèle de rémunération de ${name}`}
                            style={{ border: '1px solid var(--input-border)', borderRadius: '7px', background: 'var(--input-bg)', color: 'var(--text)', padding: '4px 7px', fontSize: '11.5px', fontFamily: 'inherit', outline: 'none' }}>
                            <option value="">Modèle par défaut</option>
                            {modeles.map(m => <option key={m.valeur} value={m.valeur}>{m.libelle}</option>)}
                          </select>
                        )}
                        {(modeleValeur || assietteValeur) && (
                          <input value={assietteValeur} onChange={e => setBaDraft(p => ({ ...p, [s.id]: e.target.value }))}
                            placeholder={modeles.find(m => m.valeur === modeleValeur)?.assiette_defaut || "assiette (suit le taux)"}
                            aria-label={`Assiette de la commission de ${name}`}
                            style={{ flex: '1 1 240px', minWidth: 0, border: '1px solid var(--input-border)', borderRadius: '7px', background: 'var(--input-bg)', color: 'var(--text)', padding: '4px 8px', fontSize: '11.5px', fontFamily: 'inherit', outline: 'none' }} />
                        )}
                        {/* Ce que le sourceur LIT réellement, une fois la cascade
                            résolue par le serveur — pas ce qu'on croit avoir réglé. */}
                        {!modifie && s.commissionBasisEffective && (
                          <span style={{ flexBasis: '100%', fontSize: '10.5px', color: 'var(--text-faint)' }}>
                            Il lit : {String(s.commissionEffective ?? '').replace('.', ',')} % {s.commissionBasisEffective}
                          </span>
                        )}
                        {modifie && (
                          <button onClick={() => saveTauxSourceur(s)} disabled={txBusy === s.id}
                            style={{ padding: '4px 10px', borderRadius: '7px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '11px', fontWeight: 600, cursor: 'pointer', opacity: txBusy === s.id ? 0.6 : 1 }}>
                            Enregistrer
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {s.status === 'pending_review' && (
                    <>
                      <button onClick={() => setStatus(s, 'active', `${name} validé — il recevra les besoins diffusés`)} disabled={busy}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-black disabled:opacity-40 transition">
                        <Check className="w-3.5 h-3.5" /> Valider
                      </button>
                      <button onClick={() => setStatus(s, 'suspended', 'Demande refusée')} disabled={busy}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-50 disabled:opacity-40 transition">
                        <X className="w-3.5 h-3.5" /> Refuser
                      </button>
                    </>
                  )}
                  {s.status === 'active' && (
                    <>
                      <span className="text-xs font-semibold text-green-700 bg-green-50 rounded-full px-2.5 py-1">Actif</span>
                      <button onClick={() => setStatus(s, 'suspended', `${name} suspendu`)} disabled={busy}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-40 transition">
                        <Ban className="w-3.5 h-3.5" /> Suspendre
                      </button>
                    </>
                  )}
                  {s.status === 'suspended' && (
                    <button onClick={() => setStatus(s, 'active', `${name} réactivé`)} disabled={busy}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-50 disabled:opacity-40 transition">
                      <RotateCcw className="w-3.5 h-3.5" /> Réactiver
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Ciblage de la diffusion : listes + besoins actuellement diffusés ── */}
      <ListesDiffusion sourceurs={sourceurs} showNotification={showNotification} />
      <DiffusionsEnCours showNotification={showNotification} />
    </div>
  );
}
